import React, { useState, useEffect } from 'react';
import {
    Dialog,
    DialogTitle,
    DialogContent,
    DialogActions,
    Button,
    TextField,
    Grid,
    FormControl,
    InputLabel,
    Select,
    MenuItem,
    FormControlLabel,
    Checkbox,
    Box,
    Typography,
    Divider,
    CircularProgress,
    Alert
} from '@mui/material';
import { RoleService } from '../api/services/roleService';
import { StaffService } from '../api/services/staffService';

const employmentTypes = ['Full-time', 'Part-time', 'Contractor'];

export default function StaffFormModal({ open, onClose, onSuccess, staffData }) {
    const [formData, setFormData] = useState({
        employeeId: '',
        fullName: '',
        departmentId: '',
        designation: '',
        employmentType: 'Full-time',
        joiningDate: new Date().toISOString().split('T')[0],
        contactInfo: {
            phone: '',
            email: '',
            emergencyContact: ''
        },
        address: '',
        createAccount: true,
        roleId: ''
    });

    const [roles, setRoles] = useState([]);
    const [departments, setDepartments] = useState([]);
    const [isSubmitting, setIsSubmitting] = useState(false);
    const [error, setError] = useState('');
    const [isLoadingRoles, setIsLoadingRoles] = useState(false);
    const [isLoadingDepts, setIsLoadingDepts] = useState(false);

    const isEditMode = !!staffData;
    const isDeactivated = isEditMode && staffData?.status !== 'Active';

    useEffect(() => {
        if (open) {
            fetchRoles();
            fetchDepartments();
            if (staffData) {
                // Fetch full details to ensure we have all fields (like roleId)
                const fetchFullDetails = async () => {
                    try {
                        const fullStaff = await StaffService.getStaffById(staffData.id || staffData._id);
                        setFormData({
                            employeeId: fullStaff.employeeId || '',
                            fullName: fullStaff.fullName || fullStaff.name || '',
                            departmentId: fullStaff.departmentId || (fullStaff.department?.id) || '',
                            designation: fullStaff.designation || '',
                            employmentType: fullStaff.employmentType || fullStaff.type || 'Full-time',
                            joiningDate: fullStaff.joiningDate ? fullStaff.joiningDate.split('T')[0] : new Date().toISOString().split('T')[0],
                            contactInfo: {
                                phone: fullStaff.contactInfo?.phone || '',
                                email: fullStaff.contactInfo?.email || '',
                                emergencyContact: fullStaff.contactInfo?.emergencyContact || ''
                            },
                            address: fullStaff.address || '',
                            createAccount: fullStaff.userAccount?.status === 'ACTIVE',
                            roleId: fullStaff.roleId || ''
                        });
                    } catch (err) {
                        console.error('Failed to pre-fill staff details:', err);
                        // Fallback to partial data if fetch fails
                        setFormData(prev => ({
                            ...prev,
                            fullName: staffData.fullName || staffData.name || '',
                            employeeId: staffData.employeeId || ''
                        }));
                    }
                };
                fetchFullDetails();
            } else {
                resetForm();
            }
        }
    }, [open, staffData]);

    const fetchRoles = async () => {
        setIsLoadingRoles(true);
        try {
            const response = await RoleService.getAll({ noPagination: true });
            const rolesList = Array.isArray(response)
                ? response
                : (response?.data || response?.roles || []);
            setRoles(Array.isArray(rolesList) ? rolesList : []);
        } catch (err) {
            console.error('Failed to fetch roles:', err);
        } finally {
            setIsLoadingRoles(false);
        }
    };

    const fetchDepartments = async () => {
        setIsLoadingDepts(true);
        try {
            const data = await StaffService.getDepartments();
            setDepartments(Array.isArray(data) ? data : []);
        } catch (err) {
            console.error('Failed to fetch departments:', err);
        } finally {
            setIsLoadingDepts(false);
        }
    };

    const handleChange = (e) => {
        let { name, value, checked, type } = e.target;
        if (name === 'contactInfo.phone') {
            value = value.replace(/\D/g, '').slice(0, 10);
        }
        if (name === 'fullName') {
            value = value.replace(/[0-9]/g, '');
        }
        
        if (name.includes('.')) {
            const [parent, child] = name.split('.');
            setFormData(prev => ({
                ...prev,
                [parent]: {
                    ...prev[parent],
                    [child]: value
                }
            }));
        } else {
            setFormData(prev => ({
                ...prev,
                [name]: type === 'checkbox' ? checked : value
            }));
        }
    };

    const resetForm = () => {
        setFormData({
            employeeId: '',
            fullName: '',
            departmentId: '',
            designation: '',
            employmentType: 'Full-time',
            joiningDate: new Date().toISOString().split('T')[0],
            contactInfo: {
                phone: '',
                email: '',
                emergencyContact: ''
            },
            address: '',
            createAccount: true,
            roleId: ''
        });
        setError('');
    };

    const handleCancel = () => {
        resetForm();
        onClose();
    };

    const handleReactivate = async () => {
        setIsSubmitting(true);
        setError('');
        try {
            const staffId = staffData.id || staffData._id;
            await StaffService.updateStaff(staffId, { status: 'Active' });
            if (onSuccess) {
                onSuccess('User account settings updated and status synchronized successfully', staffId, 'Active');
            }
            onClose();
        } catch (err) {
            setError(err.message || 'Failed to reactivate staff.');
        } finally {
            setIsSubmitting(false);
        }
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        
        if (!formData.fullName || !formData.fullName.trim()) {
            setError('Full Name is required.');
            return;
        }

        if (/[0-9]/.test(formData.fullName)) {
            setError('Full name cannot contain numbers.');
            return;
        }
        
        if (formData.createAccount && !formData.contactInfo.email) {
            setError('Email address is required to create a portal account.');
            return;
        }

        if (formData.createAccount && !formData.roleId) {
            setError('System role is required when creating a portal account.');
            return;
        }

        if (!formData.designation) {
            setError('Designation is required.');
            return;
        }
        
        if (!formData.contactInfo.phone) {
            setError('Phone number is required.');
            return;
        }

        if (formData.contactInfo.phone.length !== 10) {
            setError('Phone number must be exactly 10 digits.');
            return;
        }

        setIsSubmitting(true);
        setError('');

        try {
            if (isEditMode) {
                // PATCH mode: construct diff payload
                const updates = {};
                
                if (formData.fullName !== (staffData.fullName || staffData.name)) updates.fullName = formData.fullName;
                if (formData.designation !== staffData.designation) updates.designation = formData.designation;
                if (formData.departmentId !== staffData.departmentId) updates.departmentId = formData.departmentId;
                if (formData.employmentType !== (staffData.employmentType || staffData.type)) updates.employmentType = formData.employmentType;
                if (formData.address !== staffData.address) updates.address = formData.address;
                
                // Nested contactInfo check - Include existing fields if any field in contactInfo changed
                const hasContactChanges = 
                    formData.contactInfo.phone !== staffData.contactInfo?.phone ||
                    formData.contactInfo.email !== staffData.contactInfo?.email ||
                    formData.contactInfo.emergencyContact !== staffData.contactInfo?.emergencyContact;

                if (hasContactChanges) {
                    const contactInfo = {};
                    if (formData.contactInfo.phone) contactInfo.phone = formData.contactInfo.phone;
                    if (formData.contactInfo.email) contactInfo.email = formData.contactInfo.email;
                    if (formData.contactInfo.emergencyContact) contactInfo.emergencyContact = formData.contactInfo.emergencyContact;
                    
                    if (Object.keys(contactInfo).length > 0) {
                        updates.contactInfo = contactInfo;
                    }
                }

                updates.createAccount = formData.createAccount;

                if (formData.createAccount && formData.roleId !== staffData.roleId) {
                    if (formData.roleId) updates.roleId = formData.roleId;
                }

                // Remove empty fields to avoid passing null or empty strings
                if (updates.address === '') delete updates.address;
                if (updates.departmentId === '') delete updates.departmentId;

                if (Object.keys(updates).length === 0) {
                    onClose();
                    return;
                }

                const staffId = staffData.id || staffData._id;
                await StaffService.updateStaff(staffId, updates);
                onSuccess('Staff record updated successfully');
            } else {
                // POST mode
                if (!formData.fullName || !formData.employeeId || !formData.departmentId || !formData.designation) {
                    throw new Error('Please fill in all required fields.');
                }
                
                const payload = { ...formData, joiningDate: new Date(formData.joiningDate).toISOString() };
                
                // Remove empty fields to avoid passing null or empty strings
                if (!payload.address) delete payload.address;
                if (!payload.roleId || !payload.createAccount) {
                    delete payload.roleId;
                }
                
                const contactInfo = { ...payload.contactInfo };
                if (!contactInfo.phone) delete contactInfo.phone;
                if (!contactInfo.email) delete contactInfo.email;
                if (!contactInfo.emergencyContact) delete contactInfo.emergencyContact;
                
                if (Object.keys(contactInfo).length === 0) {
                    delete payload.contactInfo;
                } else {
                    payload.contactInfo = contactInfo;
                }

                await StaffService.addStaff(payload);
                onSuccess('Staff member added successfully');
            }
            
            resetForm();
            onClose();
        } catch (err) {
            console.error('Submission error details:', err);
            setError(err.message || 'An error occurred while saving staff data.');
        } finally {
            setIsSubmitting(false);
        }
    };

    return (
        <Dialog open={open} onClose={handleCancel} maxWidth="md" fullWidth>
            <DialogTitle sx={{ fontWeight: 'bold' }}>
                {isEditMode ? 'Edit Staff Member' : 'Add New Staff Member'}
            </DialogTitle>
            <form onSubmit={handleSubmit}>
                <DialogContent dividers>
                    {isDeactivated && (
                        <Alert severity="warning" sx={{ mb: 3 }}>
                            This user profile is currently deactivated. To modify system roles or permissions, please reactivate this account first.
                        </Alert>
                    )}
                    {error && <Alert severity="error" sx={{ mb: 3 }}>{error}</Alert>}
                    
                    <Grid container spacing={2}>
                        {/* Basic Info */}
                        <Grid item xs={12}>
                            <Typography variant="subtitle2" color="primary" fontWeight="bold" gutterBottom>
                                Basic Information
                            </Typography>
                        </Grid>
                        <Grid item xs={12} sm={6}>
                            <TextField
                                label="Full Name"
                                name="fullName"
                                value={formData.fullName}
                                onChange={handleChange}
                                fullWidth
                                required
                                size="small"
                            />
                        </Grid>
                        <Grid item xs={12} sm={6}>
                            <TextField
                                label="Employee ID"
                                name="employeeId"
                                value={formData.employeeId}
                                onChange={handleChange}
                                fullWidth
                                required
                                size="small"
                                placeholder="EMP-2024-001"
                                disabled={isEditMode}
                            />
                        </Grid>
                        
                        <Grid item xs={12} sm={4}>
                            <FormControl fullWidth size="small" required>
                                <InputLabel>Department</InputLabel>
                                <Select
                                    name="departmentId"
                                    value={formData.departmentId}
                                    onChange={handleChange}
                                    label="Department"
                                >
                                    {isLoadingDepts ? (
                                        <MenuItem disabled><CircularProgress size={20} sx={{ mr: 1 }} /> Loading departments...</MenuItem>
                                    ) : (
                                        departments.map(dept => (
                                            <MenuItem key={dept.id || dept._id} value={dept.id || dept._id}>{dept.name}</MenuItem>
                                        ))
                                    )}
                                </Select>
                            </FormControl>
                        </Grid>
                        <Grid item xs={12} sm={4}>
                            <TextField
                                label="Designation"
                                name="designation"
                                value={formData.designation}
                                onChange={handleChange}
                                fullWidth
                                required
                                size="small"
                                placeholder="Front Office Manager"
                            />
                        </Grid>
                        <Grid item xs={12} sm={4}>
                            <FormControl fullWidth size="small">
                                <InputLabel>Employment Type</InputLabel>
                                <Select
                                    name="employmentType"
                                    value={formData.employmentType}
                                    onChange={handleChange}
                                    label="Employment Type"
                                >
                                    {employmentTypes.map(type => (
                                        <MenuItem key={type} value={type}>{type}</MenuItem>
                                    ))}
                                </Select>
                            </FormControl>
                        </Grid>
                        
                        <Grid item xs={12} sm={6}>
                            <TextField
                                label="Joining Date"
                                name="joiningDate"
                                type="date"
                                value={formData.joiningDate}
                                onChange={handleChange}
                                fullWidth
                                size="small"
                                InputLabelProps={{ shrink: true }}
                            />
                        </Grid>

                        {/* Contact Info */}
                        <Grid item xs={12} sx={{ mt: 2 }}>
                            <Typography variant="subtitle2" color="primary" fontWeight="bold" gutterBottom>
                                Contact Information
                            </Typography>
                            <Divider sx={{ mb: 2 }} />
                        </Grid>
                        <Grid item xs={12} sm={6}>
                            <TextField
                                label="Phone Number"
                                name="contactInfo.phone"
                                value={formData.contactInfo.phone}
                                onChange={handleChange}
                                fullWidth
                                size="small"
                                required
                            />
                        </Grid>
                        <Grid item xs={12} sm={6}>
                            <TextField
                                label="Email Address"
                                name="contactInfo.email"
                                type="email"
                                value={formData.contactInfo.email}
                                onChange={handleChange}
                                fullWidth
                                size="small"
                                required={formData.createAccount}
                                disabled={!formData.createAccount}
                            />
                        </Grid>
                        <Grid item xs={12}>
                            <TextField
                                label="Emergency Contact"
                                name="contactInfo.emergencyContact"
                                value={formData.contactInfo.emergencyContact}
                                onChange={handleChange}
                                fullWidth
                                size="small"
                                placeholder="Name: +1234567891"
                            />
                        </Grid>
                        <Grid item xs={12}>
                            <TextField
                                label="Address"
                                name="address"
                                value={formData.address}
                                onChange={handleChange}
                                fullWidth
                                multiline
                                rows={2}
                                size="small"
                            />
                        </Grid>

                        {/* Account Access */}
                        <Grid item xs={12} sx={{ mt: 2 }}>
                            <Typography variant="subtitle2" color="primary" fontWeight="bold" gutterBottom>
                                Account Access
                            </Typography>
                            <Divider sx={{ mb: 2 }} />
                        </Grid>
                        <Grid item xs={12} sm={6}>
                            <FormControlLabel
                                control={
                                    <Checkbox
                                        name="createAccount"
                                        checked={formData.createAccount}
                                        onChange={handleChange}
                                        color="primary"
                                        disabled={isDeactivated}
                                    />
                                }
                                label="Create Portal Account"
                            />
                        </Grid>
                        <Grid item xs={12} sm={6}>
                            <FormControl fullWidth size="small" disabled={isDeactivated || !formData.createAccount} required={formData.createAccount}>
                                <InputLabel>Assign System Role</InputLabel>
                                <Select
                                    name="roleId"
                                    value={formData.roleId}
                                    onChange={handleChange}
                                    label="Assign System Role"
                                    required={formData.createAccount}
                                >
                                    {isLoadingRoles ? (
                                        <MenuItem disabled><CircularProgress size={20} sx={{ mr: 1 }} /> Loading roles...</MenuItem>
                                    ) : (
                                        roles.map(role => (
                                            <MenuItem key={role.id} value={role.id}>
                                                {typeof role.name === 'object' ? role.name?.name : role.name}
                                            </MenuItem>
                                        ))
                                    )}
                                </Select>
                            </FormControl>
                        </Grid>
                    </Grid>
                </DialogContent>
                <DialogActions sx={{ p: 2.5 }}>
                    {isDeactivated && (
                        <Button 
                            color="primary"
                            variant="contained"
                            disabled={isSubmitting}
                            onClick={handleReactivate}
                            sx={{ mr: 'auto' }}
                        >
                            Reactivate Account
                        </Button>
                    )}
                    <Button onClick={handleCancel} color="inherit">Cancel</Button>
                    <Button
                        type="submit"
                        variant="contained"
                        disabled={isSubmitting}
                        startIcon={isSubmitting && <CircularProgress size={16} />}
                        sx={{ px: 4 }}
                    >
                        {isSubmitting ? (isEditMode ? 'Saving...' : 'Adding...') : (isEditMode ? 'Save Changes' : 'Add Staff Member')}
                    </Button>
                </DialogActions>
            </form>
        </Dialog>
    );
}
